import { ActionName, CategoryName, TFormName } from '@infotech-soe/metaschema';

import { ConcurrentLRUCache } from '../../framework/caching/ConcurrentLRUCache';
import {
  getLogger,
  InjectDebugLogger,
} from '../../framework/logging/decorators/InjectDebugLogger';
import { LogFormat } from '../../framework/logging/decorators/LogFormat';
import { LogMethod } from '../../framework/logging/decorators/LogMethod';
import IndexedDBObjectStorage from '../persistence/IndexedDBObjectStorage';
import ObjectStorage from '../persistence/ObjectStorage';
import IApiProvider from '../providers/abstract/IApiProvider';
import MerpsApiProvider from '../providers/pb-adapter/MerpsApiProvider';

import IIntlStorage from './IIntlStorage';
import { StoredIntlSource } from './StoredIntlSource';

/**
 * Maximum number of entries in {@link AsyncLRUCache} of schemas.
 */
const MAX_CACHE_SIZE = 10000;

/**
 * Name of {@link ObjectStorage} store for {@link StoredIntlSource} objects.
 */
const STORAGE_NAME = 'intl';

@InjectDebugLogger('intl')
class IntlStorageSingleton implements IIntlStorage {
  private readonly cache: ConcurrentLRUCache<string, StoredIntlSource>;
  private readonly api: IApiProvider;
  private readonly storage: ObjectStorage<StoredIntlSource>;
  constructor() {
    this.api = new MerpsApiProvider();
    this.storage = new IndexedDBObjectStorage();
    this.cache = new ConcurrentLRUCache(MAX_CACHE_SIZE, this.loadIntl);
    this.storage.open(STORAGE_NAME);
  }

  @LogMethod
  public async getCategory(
    @LogFormat('%s') category: CategoryName,
    @LogFormat('%s') locale = 'uk'
  ): Promise<Record<string, any>> {
    return this.cache.getEntry(`category/${category}/${locale}`);
  }

  @LogMethod
  public async getCommon(
    @LogFormat('%s') locale = 'uk'
  ): Promise<Record<string, any>> {
    return this.cache.getEntry(`common/${locale}`);
  }

  @LogMethod
  public async getDomains(
    @LogFormat('%s') locale = 'uk'
  ): Promise<Record<string, any>> {
    return this.cache.getEntry(`domains/${locale}`);
  }

  @LogMethod
  public getAction(
    @LogFormat('%s') category: CategoryName,
    @LogFormat('%s') action: ActionName,
    @LogFormat('%s') locale = 'uk'
  ): Promise<StoredIntlSource> {
    return this.cache.getEntry(`actions/${category}/${action}/${locale}`);
  }

  @LogMethod
  public getForm(
    @LogFormat('%s') category: CategoryName,
    @LogFormat('%s') form: TFormName,
    @LogFormat('%s') locale = 'uk'
  ): Promise<StoredIntlSource> {
    return this.cache.getEntry(`forms/${category}/${form}/${locale}`);
  }

  @LogMethod
  public async purgeCache(): Promise<void> {
    this.cache.clearEntries();
    await this.storage.deleteAll();
  }

  private readonly loadIntl = async (
    name: string
  ): Promise<StoredIntlSource> => {
    const log = getLogger(IntlStorageSingleton);
    let messages: StoredIntlSource | undefined = await this.storage.get(name);

    log('messages %s not found in RAM cache, looking up in disk cache', name);

    if (messages) {
      return messages;
    }

    log(
      'messages %s not found in disk cache, trying to fetch from server',
      name
    );

    await this.fetchMessages(name);

    messages = await this.storage.get(name);

    if (messages) {
      return messages;
    }

    log('messages %s not found on server, returning empty object', name);

    return {};
  };

  private async fetchMessages(name: string): Promise<void> {
    const log = getLogger(IntlStorageSingleton);
    const nameParts = name.split('/');
    let messages: StoredIntlSource | undefined;

    switch (nameParts[0]) {
      case 'category':
        log('getting category %s messages', name);
        messages = await this.api.intl.getCategory(nameParts[1], nameParts[2]);
        break;
      case 'domains':
        log('getting domains %s messages', name);
        messages = await this.api.intl.getDomains(nameParts[1]);
        break;
      default:
        log('getting common %s messages', name);
        messages = await this.api.intl.getCommon(nameParts[1]);
    }

    if (messages) {
      await this.storage.set(name, messages);
    }
  }
}

const IntlStorage = (() => {
  let instance: IIntlStorage;

  return new Proxy(IntlStorageSingleton, {
    construct(Target: typeof IntlStorageSingleton): IIntlStorage {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default IntlStorage;
